import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { FaAngleRight } from "react-icons/fa6";
import { GoPencil } from "react-icons/go";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { getAllRestaurantsFinance } from "../../../redux/restaurantFinance/restaurantFinanceSlice";
import { formatCurrency, formatNumber } from "./data";
import Table from "./Table";

const Index = () => {
  const [activeTab, setActiveTab] = useState("earnings");
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { restaurants, status } = useSelector((state: RootState) => state.restaurantFinance);

  useEffect(() => {
    dispatch(getAllRestaurantsFinance({ page: 1, limit: 100 })); // Get all restaurants for dashboard metrics
  }, [dispatch]);

  // Calculate dashboard metrics from real data
  const dashboardMetrics = [
    {
      title: "Total Pending Payouts",
      value: formatCurrency(restaurants.reduce((sum, restaurant) => sum + (restaurant.pendingPayouts || 0), 0)),
      change: `${restaurants.length} Restaurants`,
    },
    {
      title: "Total Earnings",
      value: formatCurrency(restaurants.reduce((sum, restaurant) => sum + (restaurant.totalEarnings || 0), 0)),
      change: "All time earnings",
    },
    {
      title: "Active Restaurants",
      value: formatNumber(restaurants.filter(r => r.status === "active").length),
      change: "Currently active",
    },
    {
      title: "Total Commission",
      value: formatCurrency(restaurants.reduce((sum, restaurant) => sum + (restaurant.commission || 0), 0)),
      change: "All time commission",
    },
  ];

  return (
    <section className="font-mont">
      <div className="flex items-start gap-4">
        <button
          className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
          onClick={() => navigate(-1)}
        >
          <MdOutlineKeyboardBackspace className="w-6 h-6" />
        </button>
        <article>
          <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
            <span className="font-semibold">Restaurant</span> <FaAngleRight />
            <span className="font-semibold">Finance</span>
          </h1>
          <p className="text-sm">
            Monitor vendor earnings, process payouts, and manage financial
            adjustments.
          </p>
        </article>
      </div>
      <section className="bg-white px-4 py-7 my-5 flex gap-3 justify-between">
        {dashboardMetrics.map((item, index) => (
          <div key={index} className="w-[269px] border rounded-md p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm">{item.title}</h3>
              <div className="p-3 rounded-full border">
                <GoPencil className="w-3 h-3" />
              </div>
            </div>
            <p className="text-lg font-medium">{item.value}</p>
            <p className="text-xs text-gray-500">{item.change}</p>
          </div>
        ))}
      </section>
      <section className="bg-[#EEEEEE40] px-3 py-5 my-3">
        <nav className="bg-white p-5 w-fit">
          <ul className="flex gap-10 text-base font-medium">
            {
              <li>
                <button
                  type="button"
                  className={`cursor-pointer p-2 ${
                    activeTab === "earnings" && "bg-[#EEEEEE40]"
                  }`}
                  onClick={() => setActiveTab("earnings")}
                >
                  Restaurant Earnings
                </button>
              </li>
            }
            <li>
              <button
                type="button"
                className={`cursor-pointer p-2 ${
                  activeTab === "payouts" && "bg-[#EEEEEE40]"
                }`}
                onClick={() => setActiveTab("payouts")}
              >
                Payouts History
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`cursor-pointer p-2 ${
                  activeTab === "suspended" && "bg-[#EEEEEE40]"
                }`}
                onClick={() => setActiveTab("suspended")}
              >
               Suspended Restaurant Payout
              </button>
            </li>
          </ul>
        </nav>
      </section>
      <>
        {activeTab === "earnings" ? (
          <Table table="earnings" />
        ) : activeTab === "payouts" ? (
          <Table table="payouts" />
        ) : null}
      </>
    </section>
  );
};

export default Index;
